import { useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { FilePlus2, FileSignature, Search } from "lucide-react";
import { api, useApi } from "../lib/api";
import { when } from "../lib/format";
import type { Envelope } from "../lib/types";
import { StatusChip, inputCls } from "../components/bits";
import { Button, cx, Empty, ErrorNote, Spinner, toast } from "../components/ui";

const TABS = [
  { key: "all", label: "All" },
  { key: "waiting", label: "Waiting", match: (e: Envelope) => e.status === "sent" || e.status === "completing" },
  { key: "completed", label: "Completed", match: (e: Envelope) => e.status === "completed" },
  { key: "draft", label: "Drafts", match: (e: Envelope) => e.status === "draft" },
  { key: "ended", label: "Declined / voided / expired", match: (e: Envelope) => ["declined", "voided", "expired"].includes(e.status) },
] as const;

export default function Dashboard() {
  const { data, error, loading } = useApi<{ envelopes: Envelope[] }>("/envelopes");
  const [tab, setTab] = useState<(typeof TABS)[number]["key"]>("all");
  const [q, setQ] = useState("");
  const [creating, setCreating] = useState(false);
  const nav = useNavigate();

  const list = useMemo(() => {
    const t = TABS.find((x) => x.key === tab)!;
    const needle = q.trim().toLowerCase();
    return (data?.envelopes ?? []).filter((e) => (!("match" in t) || t.match(e)) && (!needle || e.title.toLowerCase().includes(needle) || (e.fileName ?? "").toLowerCase().includes(needle)));
  }, [data, tab, q]);

  const create = async () => {
    setCreating(true);
    try {
      const env = await api<Envelope>("/envelopes", { body: { title: "Untitled" } });
      nav(`/envelopes/${env.id}`);
    } catch (e) {
      toast((e as Error).message, "error");
      setCreating(false);
    }
  };

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <div className="flex items-center justify-between gap-3 mb-5">
        <h1 className="text-xl font-semibold">Envelopes</h1>
        <Button variant="primary" busy={creating} onClick={create}>
          <FilePlus2 className="size-4" /> New envelope
        </Button>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-4">
        <div className="flex gap-1 overflow-x-auto -mx-1 px-1" role="tablist">
          {TABS.map((t) => (
            <button key={t.key} role="tab" aria-selected={tab === t.key} onClick={() => setTab(t.key)} className={cx("px-3 h-8 rounded-full text-sm font-medium whitespace-nowrap", tab === t.key ? "bg-accent-chip text-accent-text" : "text-ink-2 hover:bg-surface-2")}>
              {t.label}
            </button>
          ))}
        </div>
        <div className="relative sm:ml-auto sm:w-64">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted" aria-hidden />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search titles" aria-label="Search titles" className={cx(inputCls, "pl-9")} />
        </div>
      </div>
      {error && <ErrorNote>{error}</ErrorNote>}
      {loading && !data ? (
        <Spinner />
      ) : list.length === 0 ? (
        <Empty icon={<FileSignature className="size-10" />} title={data?.envelopes.length ? "Nothing here" : "No envelopes yet"}>
          {data?.envelopes.length ? "Try another tab or search." : "Create an envelope, upload a PDF, add signers and place their fields."}
        </Empty>
      ) : (
        <ul className="bg-surface border border-line rounded-xl divide-y divide-line overflow-hidden">
          {list.map((e) => (
            <li key={e.id}>
              <Link to={`/envelopes/${e.id}`} className="flex items-center gap-3 px-4 py-3 hover:bg-surface-2">
                <div className="min-w-0 flex-1">
                  <div className="font-medium text-ink truncate">{e.title}</div>
                  <div className="text-xs text-muted truncate">
                    {e.status === "draft" ? `Edited ${when(e.updatedAt)}` : `Sent ${when(e.sentAt)}`}
                    {e.signerCount > 0 && e.status !== "draft" && ` · ${e.signedCount} of ${e.signerCount} signed`}
                    {e.fileName && ` · ${e.fileName}`}
                  </div>
                </div>
                <StatusChip status={e.status} />
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
